import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, InteractionType, RedirectRequest } from '@azure/msal-browser';
import { filter, Observable, of, take, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly _msalService = inject(MsalService);
  private readonly _msalBroadcastService = inject(MsalBroadcastService);
  private readonly _isInProgress = signal<boolean>(true);
  private readonly _accountInfo = signal<AccountInfo | undefined>(undefined);
  private readonly _redirectError = signal<any>(undefined);
  private readonly _scopes = environment.apiConfig.scopes;

  public readonly accountInfo = computed(() => this._accountInfo());
  public readonly isReady = computed(() => !this._isInProgress());
  public readonly isAuthenticated = computed(() => this._accountInfo() != null);

  constructor() {
    const msalInstance = this._msalService.instance;
    // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    msalInstance.enableAccountStorageEvents();

    // Initial hydration
    const existing = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (existing) {
      this._accountInfo.set(existing);
      msalInstance.setActiveAccount(existing);
    }

    this._msalService.handleRedirectObservable()
      .pipe(
        takeUntilDestroyed(),
      )
      .subscribe({
        error: error => this._redirectError.set(error)
      });

    // Handle redirect + other success events
    this._msalBroadcastService.msalSubject$.pipe(
      takeUntilDestroyed(),
      filter((msg: EventMessage) =>
        msg.eventType === EventType.LOGIN_SUCCESS ||
        msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        msg.eventType === EventType.ACCOUNT_ADDED ||
        msg.eventType === EventType.ACCOUNT_REMOVED
      ),
    ).subscribe((msg: EventMessage) => {
      const payload = msg.payload as AuthenticationResult;
      if (msg.interactionType === InteractionType.Redirect && payload.account) {
        msalInstance.setActiveAccount(payload.account);
      }

      const active = this.getActiveAccount() ?? payload.account;
      if (active && this._accountInfo()?.username !== active.username) {
        this._accountInfo.set(active);
      }

      if (msg.eventType === EventType.ACCOUNT_REMOVED && !this.getActiveAccount()) {
        this._accountInfo.set(undefined);
      }
    });

    // Track interaction readiness (optional separate readiness)
    this._msalBroadcastService.inProgress$
      .pipe(
        takeUntilDestroyed(),
        tap(status => this._isInProgress.set(status !== InteractionStatus.None))
      )
      .subscribe();

    // Optional reconciliation effect (Angular signals effect):
    effect(() => {
      const active = this.getActiveAccount();
      if (active && this._accountInfo()?.username !== active.username) {
        this._accountInfo.set(active);
      }
    });
  }

  async initialize(): Promise<any> {
    await this._msalService.instance.initialize();
    this.acquireTokenSilent(this._scopes).pipe(
      take(1),
      tap((result: AuthenticationResult | null) => {
        this._accountInfo.set(result?.account);
      })
    ).subscribe();
  }

  acquireTokenSilent(scopes: string[]): Observable<AuthenticationResult | null> {
    const activeAccount = this.getActiveAccount();
    if (activeAccount == null) {
      return of(null);
    }

    return this._msalService.acquireTokenSilent({
      scopes,
      account: activeAccount
    });
  }

  login(): void {
    const activeAccount = this.getActiveAccount();
    if (activeAccount == null) {
      this._msalService.loginRedirect({
        ...this._msalGuardConfig.authRequest,
        scopes: this._scopes,
        prompt: "select_account"
      } as RedirectRequest);
    } else {
      this._accountInfo.set(activeAccount);
    }
  }

  logout(): void {
    const activeAccount = this.getActiveAccount();
    if (activeAccount != null) {
      this._msalService.logout({ account: activeAccount });
    }

    this._accountInfo.set(undefined);
  }

  switchAccount(): void {
    this._msalService.loginRedirect({
      ...this._msalGuardConfig.authRequest,
      scopes: this._scopes,
      prompt: "select_account"
    } as RedirectRequest);
  }

  isInRole(role: string | string[]): boolean {
    const account = this._accountInfo();

    if (account == null) {
      return false;
    }

    if (Array.isArray(role)) {
      return role.some(r => account.idTokenClaims?.roles?.includes(r));
    }

    return account.idTokenClaims?.roles?.includes(role) ?? false;
  }

  private getActiveAccount(): AccountInfo | null {
    return this._msalService.instance.getActiveAccount();
  }
}
